import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import ThemedTable from "../../components/ThemedTable";
import Loader from "../../components/Loader";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { apiClient } from "../../apiclient/apiclient";
import {
  Download,
  Search,
  Package,
  AlertTriangle,
  Activity,
  Trello,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Pagination from "../../components/Pagination";

/* ================= STATUS BADGE ================= */
const getStockBadge = (status) => {
  const styles = {
    "In Stock": "bg-green-100 text-green-700",
    "Low Stock": "bg-orange-100 text-orange-700",
    "Out of Stock": "bg-red-100 text-red-700",
  };
  return styles[status] || "bg-gray-100 text-gray-700";
};

const getExpiryDateStyle = (expiryDate) => {
  if (!expiryDate) return "text-slate-400 italic font-normal";
  const today = moment().startOf("day");
  const expiry = moment(expiryDate).startOf("day");
  const diffDays = expiry.diff(today, "days");
  if (diffDays < 0) return "text-red-600 font-semibold bg-red-50 border border-red-100 rounded px-1.5 py-0.5 inline-block";
  if (diffDays <= 7) return "text-orange-600 font-semibold bg-orange-50 border border-orange-100 rounded px-1.5 py-0.5 inline-block";
  if (diffDays <= 30) return "text-amber-600 font-semibold bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 inline-block";
  return "text-slate-600 font-medium";
};

const formatCurrency = (val) => {
  const n = Number(val || 0);
  return (
    "₹ " +
    n.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })
  );
};

const InventoryReports = () => {
  const [cards, setCards] = useState({});
  const [productChart, setProductChart] = useState([]);
const [expiryChart, setExpiryChart] = useState([]);
const [dailyExpiryData, setDailyExpiryData] = useState({});
  const [statusChart, setStatusChart] = useState({});
  const [table, setTable] = useState([]);
  const [search, setSearch] = useState("");
  const [pageSize] = useState(200);
  const [currentPage, setCurrentPage] = useState(1);
const [selectedMonth, setSelectedMonth] = useState(null);
const [selectedDay, setSelectedDay] = useState(null);
const [expiryCards, setExpiryCards] = useState({});
const [dailyExpiryChart, setDailyExpiryChart] = useState([]);
const [expandedRowName, setExpandedRowName] = useState(null);
const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
const [expiryFilter, setExpiryFilter] = useState("all");
const [stockFilter, setStockFilter] = useState("all");
const [warehouseFilter, setWarehouseFilter] = useState("all");
const [loading, setLoading] = useState(false);






  /* ================= FETCH REPORT ================= */
  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/inventory-report");
      setCards(res.data.cards || {});
      setProductChart(res.data.productChart || []);
      setExpiryChart(res.data.expiryChart || []);
      setDailyExpiryData(res.data.dailyExpiryData || {});
      setExpiryCards(res.data.expiryCards || {});
      setStatusChart(res.data.statusChart || {});
      setTable(res.data.table || []);
    } catch (error) {
      console.error("Inventory fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  /* ================= FILTERING & PAGINATION ================= */
  const safeLower = (value) => String(value ?? "").toLowerCase();

  const filteredTable = useMemo(() => {
    return table.filter((row) => {
      // 1. Search filter
      const q = safeLower(search);
      const formattedExpiry = row.expiryDate ? moment(row.expiryDate).format("DD-MM-YYYY") : "n/a";
      const matchesWarehouseSearch = row.warehouses?.some((w) => safeLower(w.name).includes(q));

      const matchesSearch = !q || (
        safeLower(row.productName).includes(q) ||
        safeLower(row.category).includes(q) ||
        safeLower(row.brand).includes(q) ||
        safeLower(row.status).includes(q) ||
        safeLower(row.batchId).includes(q) ||
        safeLower(formattedExpiry).includes(q) ||
        safeLower(row.price).includes(q) ||
        safeLower(row.gst).includes(q) ||
        safeLower(row.servings).includes(q) ||
        safeLower(row.servingDuration).includes(q) ||
        matchesWarehouseSearch
      );

      // 2. Expiry status filter
      let matchesExpiry = true;
      if (expiryFilter !== "all") {
        const hasExpiringBatch = row.warehouses?.some((w) => {
          if (!w.expiryDate || Number(w.quantity || 0) <= 0) return false;
          const diffDays = moment(w.expiryDate).diff(moment(), "days");
          if (expiryFilter === "expired") return diffDays < 0;
          if (expiryFilter === "expiring7") return diffDays >= 0 && diffDays <= 7;
          if (expiryFilter === "expiring30") return diffDays >= 0 && diffDays <= 30;
          if (expiryFilter === "expiring90") return diffDays >= 0 && diffDays <= 90;
          return false;
        });
        matchesExpiry = !!hasExpiringBatch;
      }

      // 3. Stock Status filter
      let matchesStock = true;
      if (stockFilter !== "all") {
        matchesStock = row.status === stockFilter;
      }

      // 4. Warehouse stock filter
      let matchesWarehouse = true;
      if (warehouseFilter !== "all") {
        matchesWarehouse = row.warehouses?.some((w) => w.name === warehouseFilter && w.quantity > 0);
      }

      // 5. Selected Day / Month graph filter
      let matchesGraph = true;
      if (selectedMonth) {
        if (selectedDay) {
          matchesGraph = row.warehouses?.some((w) => {
            if (!w.expiryDate || Number(w.quantity || 0) <= 0) return false;
            const wMonth = moment(w.expiryDate).format("MMM YYYY");
            const wDayMonth = moment(w.expiryDate).format("DD MMM");
            return wDayMonth === selectedDay && wMonth === selectedMonth;
          });
        } else {
          matchesGraph = row.warehouses?.some((w) => {
            if (!w.expiryDate || Number(w.quantity || 0) <= 0) return false;
            const wMonth = moment(w.expiryDate).format("MMM YYYY");
            return wMonth === selectedMonth;
          });
        }
      }

      return matchesSearch && matchesExpiry && matchesStock && matchesWarehouse && matchesGraph;
    });
  }, [table, search, expiryFilter, stockFilter, warehouseFilter, selectedMonth, selectedDay]);

  const warehouseNames = useMemo(() => {
    const names = new Set();
    table.forEach((row) => {
      row.warehouses?.forEach((w) => {
        if (w.name) names.add(w.name);
      });
    });
    return Array.from(names).sort();
  }, [table]);



  const totalPages = Math.ceil(filteredTable.length / pageSize);
  const paginatedTable = filteredTable.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  /* ================= DOWNLOAD ================= */
  /* ================= DOWNLOADS ================= */
  const downloadExcel = async () => {
    try {
      setIsDownloadingExcel(true);
      const params = new URLSearchParams();
      params.append("download", "true");
      if (search) params.append("search", search);
      if (expiryFilter !== "all") params.append("expiryFilter", expiryFilter);
      if (stockFilter !== "all") params.append("stockFilter", stockFilter);
      if (warehouseFilter !== "all") params.append("warehouseFilter", warehouseFilter);
      if (selectedMonth) params.append("selectedMonth", selectedMonth);
      if (selectedDay) params.append("selectedDay", selectedDay);

      const res = await apiClient.get(`/inventory-report?${params.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "InventoryReport.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      let errorMsg = "Failed to download report due to a network error.";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.message) errorMsg = parsed.message;
        } catch (e) {
          // Fallback if blob text is not valid JSON
        }
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      alert(errorMsg);
    } finally {
      setIsDownloadingExcel(false);
    }
  };


  return (
    <div className="bg-slate-50 min-h-screen">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
        <h1 className="text-xl font-bold text-slate-800">Inventory Overview</h1>
        <p className="text-sm text-slate-500 mt-1">
          Real-time stock levels • Warehouse distribution
        </p>
      </div>

      {/* ================= KPI CARDS ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {/* TOTAL PRODUCTS */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-indigo-500 to-blue-600 border-e border-b">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">
            Total Products
          </p>
          <h2 className="text-2xl font-bold mt-2 relative z-10">
           {cards.totalProducts || 0}
          </h2>
          <div className="mt-2 flex items-center gap-1.5 relative z-10">
            <Package size={14} className="opacity-70" />
            <span className="text-xs opacity-80 font-medium">Unique SKUs</span>
          </div>
        </div>

        {/* TOTAL STOCK */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 border-e border-b">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">
            Total Stock
          </p>
          <h2 className="text-2xl font-bold mt-2 relative z-10">
            {cards.totalStock?.toLocaleString() || 0}
          </h2>
          <div className="mt-2 flex items-center gap-1.5 relative z-10">
            <Activity size={14} className="opacity-70" />
            <span className="text-xs opacity-80 font-medium">
              Units available
            </span>
          </div>
        </div>

        {/* LOW STOCK */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-orange-400 to-amber-500 border-e border-b">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">
            Low Stock
          </p>
          <h2 className="text-2xl font-bold mt-2 relative z-10">
            {cards.lowStockCount || 0}
          </h2>
          <div className="mt-2 flex items-center gap-1.5 relative z-10">
            <AlertTriangle size={14} className="opacity-70" />
            <span className="text-xs opacity-80 font-medium">Needs refill</span>
          </div>
        </div>

        {/* OUT OF STOCK */}
        <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-red-500 to-pink-600 border-b">
          <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
          <p className="text-sm opacity-80 relative z-10 font-semibold uppercase tracking-wider">
            Out of Stock
          </p>
          <h2 className="text-2xl font-bold mt-2 relative z-10">
            {cards.outOfStockCount || 0}
          </h2>
          <div className="mt-2 flex items-center gap-1.5 relative z-10">
            <Trello size={14} className="opacity-70" />
            <span className="text-xs opacity-80 font-medium">
              Zero availability
            </span>
          </div>
        </div>
      </div>

      {/* ================= CHARTS SIDE BY SIDE ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* PRODUCT STOCK BAR CHART */}
        <div className="bg-white border-b border-e p-4 flex flex-col">
          <div className="flex items-center justify-between mb-6 px-2">
            <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">
              Top Products by Stock
            </h3>
            <div className="h-1 w-12 bg-[#F69314] rounded-full"></div>
          </div>

          <div className="h-[300px] flex items-center justify-center">
            {loading ? (
              <Loader text="Loading chart data..." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={productChart}
                  margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                  barCategoryGap="25%"
                  style={{ outline: "none" }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={{ stroke: "#f1f5f9" }}
                    tickLine={false}
                    dy={10}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "#f8fafc" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xl">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                            {payload[0].payload.name}
                          </p>
                          <p className="text-lg font-black text-[#172554]">
                            {payload[0].value}{" "}
                            <span className="text-xs text-slate-300 font-normal tracking-wide">
                              Units
                            </span>
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="quantity" radius={[4, 4, 0, 0]} barSize={40}>
                    {productChart.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index % 2 === 0 ? "#172554" : "#F69314"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* STOCK STATUS PIE CHART */}
        <div className="bg-white border-b p-4 flex flex-col">
          <div className="flex items-center justify-between mb-6 px-2">
            <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">
              Stock Status Distribution
            </h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  In Stock
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  Low
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  Out
                </span>
              </div>
            </div>
          </div>

          <div className="h-[300px] flex items-center justify-center">
            {loading ? (
              <Loader text="Loading chart data..." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart style={{ outline: "none" }}>
                  <Pie
                    data={[
                      { name: "In Stock", value: statusChart.inStock || 0 },
                      { name: "Low Stock", value: statusChart.lowStock || 0 },
                      {
                        name: "Out of Stock",
                        value: statusChart.outOfStock || 0,
                      },
                    ]}
                    dataKey="value"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                  >
                    <Cell fill="#22c55e" stroke="none" />
                    <Cell fill="#F69314" stroke="none" />
                    <Cell fill="#ef4444" stroke="none" />
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-[#172554] text-white rounded-lg px-4 py-2 shadow-2xl border border-white/10">
                          <p className="text-[10px] font-bold opacity-50 uppercase tracking-widest">
                            {payload[0].name}
                          </p>
                          <p className="text-base font-black italic">
                            {payload[0].value} SKUs
                          </p>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
      {/* ================= EXPIRY TIMELINE SECTION ================= */}
      <div className="grid grid-cols-1 xl:grid-cols-12">
        {/* CHART CONTAINER (COL SPAN 9) */}
        <div className="xl:col-span-9 bg-white border-b border-e p-5 flex flex-col">
          {/* HEADER */}
          <div className="flex items-center justify-between mb-6 px-2">
            <div>
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">
                {selectedMonth ? `Daily Expiry Trend — ${selectedMonth}` : "Monthly Expiry Analytics"}
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                {selectedMonth 
                  ? "Day-wise product expiry distribution" 
                  : "Click anywhere on the chart to view day-wise trend"
                }
              </p>
            </div>
            {selectedMonth ? (
              <button
                onClick={() => {
                  setSelectedMonth(null);
                  setSelectedDay(null);
                  setDailyExpiryChart([]);
                }}
                className="text-xs bg-[#172554] text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-[#1e2f6a] transition"
              >
                Back to Monthly
              </button>
            ) : (
              <div className="h-1 w-12 bg-[#F69314] rounded-full"></div>
            )}
          </div>

          {/* DYNAMIC CHART DISPLAY */}
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              {selectedMonth ? (
                /* DAILY BAR CHART */
                <BarChart
                  data={dailyExpiryChart}
                  margin={{ top: 0, right: 10, left: -20, bottom: 0 }}
                  barCategoryGap="25%"
                  style={{ cursor: "pointer", outline: "none" }}
                  onClick={(data) => {
                    if (data && data.activeLabel) {
                      setSelectedDay(data.activeLabel);
                      setTimeout(() => {
                        document.getElementById("inventory-table")?.scrollIntoView({ behavior: "smooth" });
                      }, 100);
                    }
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={{ stroke: "#f1f5f9" }}
                    tickLine={false}
                    dy={10}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "#f8fafc" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xl">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                            {payload[0].payload.date}
                          </p>
                          <p className="text-lg font-black text-[#172554]">
                            {payload[0].value}{" "}
                            <span className="text-xs text-slate-300 font-normal tracking-wide">
                              Products
                            </span>
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#F69314"
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              ) : (
                /* MONTHLY AREA CHART */
                <AreaChart
                  data={expiryChart}
                  margin={{ top: 0, right: 10, left: -20, bottom: 0 }}
                  style={{ cursor: "pointer", outline: "none" }}
                  onClick={(data) => {
                    if (data && data.activeLabel) {
                      setSelectedMonth(data.activeLabel);
                      setSelectedDay(null);
                      setDailyExpiryChart(dailyExpiryData[data.activeLabel] || []);
                    }
                  }}
                >
                  <defs>
                    <linearGradient id="monthlyExpiryGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#172554" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#172554" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={{ stroke: "#f1f5f9" }}
                    tickLine={false}
                    dy={10}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={false}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-2xl">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                            {payload[0].payload.month}
                          </p>
                          <p className="text-lg font-black text-[#172554]">
                            {payload[0].value}{" "}
                            <span className="text-xs text-slate-300 font-normal tracking-wide">
                              Products
                            </span>
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#172554"
                    strokeWidth={3}
                    fill="url(#monthlyExpiryGradient)"
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* VERTICAL CARDS (COL SPAN 3) */}
        <div className="xl:col-span-3 grid grid-cols-2 sm:grid-cols-4 xl:flex xl:flex-col bg-white">
          {/* EXPIRED */}
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-red-600 to-rose-700 border-b border-e flex-1 flex flex-col justify-between min-h-[95px]">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
            <div>
              <p className="text-[10px] opacity-80 relative z-10 font-bold uppercase tracking-wider">
                Expired
              </p>
              <h2 className="text-2xl font-bold mt-1 relative z-10">
                {loading ? (
                  <span className="inline-block w-12 h-7 bg-white/25 animate-pulse rounded"></span>
                ) : (
                  expiryCards.expiredCount || 0
                )}
              </h2>
            </div>
            <div className="mt-1 flex items-center gap-1.5 relative z-10">
              <AlertTriangle size={13} className="opacity-70" />
              <span className="text-[11px] opacity-80 font-medium">Needs disposal</span>
            </div>
          </div>

          {/* EXPIRING IN 7 DAYS */}
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-b border-e flex-1 flex flex-col justify-between min-h-[95px]">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
            <div>
              <p className="text-[10px] opacity-80 relative z-10 font-bold uppercase tracking-wider">
                Expiring in 7 Days
              </p>
              <h2 className="text-2xl font-bold mt-1 relative z-10">
                {loading ? (
                  <span className="inline-block w-12 h-7 bg-white/25 animate-pulse rounded"></span>
                ) : (
                  expiryCards.expiring7Days || 0
                )}
              </h2>
            </div>
            <div className="mt-1 flex items-center gap-1.5 relative z-10">
              <AlertTriangle size={13} className="opacity-70" />
              <span className="text-[11px] opacity-80 font-medium">Action required</span>
            </div>
          </div>

          {/* EXPIRING IN 30 DAYS */}
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-amber-400 to-yellow-500 border-b border-e flex-1 flex flex-col justify-between min-h-[95px]">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
            <div>
              <p className="text-[10px] opacity-80 relative z-10 font-bold uppercase tracking-wider">
                Expiring in 30 Days
              </p>
              <h2 className="text-2xl font-bold mt-1 relative z-10">
                {loading ? (
                  <span className="inline-block w-12 h-7 bg-white/25 animate-pulse rounded"></span>
                ) : (
                  expiryCards.expiring30Days || 0
                )}
              </h2>
            </div>
            <div className="mt-1 flex items-center gap-1.5 relative z-10">
              <Package size={13} className="opacity-70" />
              <span className="text-[11px] opacity-80 font-medium">Monitor turnover</span>
            </div>
          </div>

          {/* EXPIRING IN 90 DAYS */}
          <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 border-b border-e xl:border-e-0 flex-1 flex flex-col justify-between min-h-[95px]">
            <div className="absolute inset-0 bg-white/10 blur-2xl rounded-full -top-10 -left-10 w-40 h-40"></div>
            <div>
              <p className="text-[10px] opacity-80 relative z-10 font-bold uppercase tracking-wider">
                Expiring in 90 Days
              </p>
              <h2 className="text-2xl font-bold mt-1 relative z-10">
                {loading ? (
                  <span className="inline-block w-12 h-7 bg-white/25 animate-pulse rounded"></span>
                ) : (
                  expiryCards.expiring90Days || 0
                )}
              </h2>
            </div>
            <div className="mt-1 flex items-center gap-1.5 relative z-10">
              <Activity size={13} className="opacity-70" />
              <span className="text-[11px] opacity-80 font-medium">Long-term forecast</span>
            </div>
          </div>
        </div>
      </div>
      {/* ================= CONTROL BAR ================= */}
      <div className="border-t border-b bg-[#EFF6FF] p-3">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {/* SEARCH */}
          <div className="relative w-full sm:flex-1">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-slate-400" />
            </span>
            <input
              type="text"
              placeholder="Search by Product, Category, Brand..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm"
            />
          </div>

          {/* FILTERS */}
          <div className="flex flex-wrap sm:flex-nowrap gap-2 w-full sm:w-auto">
            {/* FILTER BY EXPIRY */}
            <select
              value={expiryFilter}
              onChange={(e) => setExpiryFilter(e.target.value)}
              className="w-full sm:w-40 px-2.5 py-2 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm text-slate-700 font-semibold"
            >
              <option value="all">All Expiry</option>
              <option value="expired">Expired</option>
              <option value="expiring7">Expiring in 7 Days</option>
              <option value="expiring30">Expiring in 30 Days</option>
              <option value="expiring90">Expiring in 90 Days</option>
            </select>

            {/* FILTER BY STOCK STATUS */}
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="w-full sm:w-36 px-2.5 py-2 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm text-slate-700 font-semibold"
            >
              <option value="all">All Stock</option>
              <option value="In Stock">In Stock</option>
              <option value="Low Stock">Low Stock</option>
              <option value="Out of Stock">Out of Stock</option>
            </select>

            {/* FILTER BY WAREHOUSE */}
            <select
              value={warehouseFilter}
              onChange={(e) => setWarehouseFilter(e.target.value)}
              className="w-full sm:w-40 px-2.5 py-2 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm text-slate-700 font-semibold"
            >
              <option value="all">All Warehouses</option>
              {warehouseNames.map((wName) => (
                <option key={wName} value={wName}>
                  {wName}
                </option>
              ))}
            </select>
          </div>

          {/* ACTIONS */}
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={fetchReport}
              disabled={loading}
              className="w-9 h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
              title="Refresh Report"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Search size={18} />
              )}
            </button>

            <button
              onClick={downloadExcel}
              className="w-9 h-9 bg-[#172554] text-white rounded-lg hover:bg-[#1e2f6a] transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0"
              title="Download Excel"
            >
              {isDownloadingExcel ? (
                <Loader inline={true} text="" />
              ) : (
                <Download size={18} />
              )}
            </button>      
          </div>
        </div>
      </div>

      {/* ================= TABLE INFO BAR ================= */}
      <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center justify-between" id="inventory-table">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            Inventory Details
          </h3>
          <span className="px-2 py-0.5 bg-blue-50 text-[#172554] text-[10px] font-bold rounded-full uppercase tracking-wider border border-blue-100">
            {filteredTable.length} Records
          </span>
          {(selectedMonth || selectedDay) && (
            <span className="flex items-center gap-1.5 px-2.5 py-0.5 bg-orange-50 text-orange-700 text-[10px] font-bold rounded-full border border-orange-100/70 shadow-sm animate-in fade-in zoom-in duration-200">
              Graph Filter: {selectedDay ? `${selectedDay} ` : ""}{selectedMonth}
              <button 
                onClick={() => {
                  setSelectedDay(null);
                  setSelectedMonth(null);
                }}
                className="hover:text-orange-950 font-bold ml-1"
                title="Clear graph filter"
              >
                ✕
              </button>
            </span>
          )}
        </div>
      </div>

      {/* ================= INVENTORY TABLE ================= */}
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200">
        <ThemedTable className="w-full text-[13px]">
          <thead>
            <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px]">
              <th className="p-3 text-center w-8"></th>
              <th className="p-3 text-center w-10 hidden md:table-cell whitespace-nowrap">#</th>
              <th className="p-3 text-left whitespace-nowrap">Product</th>
              <th className="p-3 text-left hidden lg:table-cell whitespace-nowrap">Category</th>
              <th className="p-3 text-left hidden md:table-cell whitespace-nowrap">Brand</th>
              <th className="p-3 text-center whitespace-nowrap">Total Qty</th>
              <th className="p-3 text-center whitespace-nowrap">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7 + warehouseNames.length}
                  className="py-20 text-center"
                >
                  <Loader text="Loading inventory records..." />
                </td>
              </tr>
            ) : paginatedTable.length > 0 ? (
              paginatedTable.map((row, index) => {
                const isExpanded = expandedRowName === row.productName;
                return (
                  <React.Fragment key={index}>
                    <tr
                      className="bg-white hover:bg-slate-100/80 transition-colors border-b border-slate-200 last:border-0 cursor-pointer select-none"
                      onClick={() => setExpandedRowName(isExpanded ? null : row.productName)}
                    >
                      <td className="px-2 py-3 text-center text-slate-400">
                        {isExpanded ? (
                          <ChevronUp size={16} className="text-[#172554]" />
                        ) : (
                          <ChevronDown size={16} className="text-slate-400" />
                        )}
                      </td>
                      <td className="px-2 py-3 text-center text-slate-400 font-medium hidden md:table-cell whitespace-nowrap">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-2 py-3 font-medium text-[#172554] whitespace-nowrap">
                        <div
                          className="max-w-[200px] sm:max-w-[300px] truncate"
                          title={row.productName}
                        >
                          {row.productName}
                        </div>
                      </td>
                      <td className="px-2 py-3 text-slate-500 hidden lg:table-cell whitespace-nowrap">
                        {row.category}
                      </td>
                      <td className="px-2 py-3 text-slate-500 hidden md:table-cell whitespace-nowrap">
                        {row.brand}
                      </td>
                      <td className="px-2 py-3 text-center font-semibold text-slate-900 whitespace-nowrap">
                        {row.totalQuantity?.toLocaleString()}
                      </td>
                      <td className="px-2 py-3 text-center whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${getStockBadge(row.status)}`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-50/75 border-b border-slate-200">
                        <td colSpan={7 + warehouseNames.length} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 text-[13px] text-slate-700">
                            {(() => {
                              const activeExpiries = (row.warehouses || [])
                                .filter((w) => w.expiryDate && Number(w.quantity || 0) > 0)
                                .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate));

                              const getBatchExpiryBadge = (expiryDate) => {
                                const today = moment().startOf("day");
                                const expiry = moment(expiryDate).startOf("day");
                                const diffDays = expiry.diff(today, "days");

                                if (diffDays < 0) {
                                  return {
                                    text: "Expired",
                                    style: "bg-red-50 text-red-700 border-red-100",
                                  };
                                }
                                if (diffDays <= 7) {
                                  return {
                                    text: "Expiring in 7 Days",
                                    style: "bg-orange-50 text-orange-700 border-orange-100",
                                  };
                                }
                                if (diffDays <= 30) {
                                  return {
                                    text: "Expiring in 30 Days",
                                    style: "bg-amber-50 text-amber-700 border-amber-100",
                                  };
                                }
                                if (diffDays <= 90) {
                                  return {
                                    text: "Expiring in 90 Days",
                                    style: "bg-blue-50 text-[#172554] border-blue-100",
                                  };
                                }
                                return {
                                  text: "Safe",
                                  style: "bg-emerald-50 text-emerald-700 border-emerald-100",
                                };
                              };

                              const warehouseDistribution = {};
                              (row.warehouses || []).forEach((w) => {
                                if (Number(w.quantity || 0) <= 0) return;
                                const name = w.name || "N/A";
                                if (!warehouseDistribution[name]) {
                                  warehouseDistribution[name] = 0;
                                }
                                warehouseDistribution[name] += Number(w.quantity || 0);
                              });
                              const distList = Object.entries(warehouseDistribution);

                              return (
                                <>
                                  {/* UPCOMING EXPIRY TIMELINE */}
                                  <div className="md:col-span-5 flex flex-col gap-2 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
                                    <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                      Upcoming Expiries
                                    </h4>
                                    <div className="flex flex-col gap-2.5">
                                      {activeExpiries.length > 0 ? (
                                        activeExpiries.map((w, idx) => {
                                          const badge = getBatchExpiryBadge(w.expiryDate);
                                          const batchText = w.batchId && w.batchId !== "N/A" ? ` [Batch: ${w.batchId}]` : "";
                                          return (
                                            <div key={idx} className="flex flex-col gap-1 pb-2 border-b border-slate-50 last:border-b-0 last:pb-0">
                                              <div className="flex justify-between items-center">
                                                <span className="font-bold text-[#172554] text-[11.5px]">
                                                  {moment(w.expiryDate).format("DD-MM-YYYY")}
                                                </span>
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${badge.style}`}>
                                                  {badge.text}
                                                </span>
                                              </div>
                                              <div className="flex justify-between items-center text-[10.5px] text-slate-500">
                                                <span>{w.name}{batchText}</span>
                                                <span className="font-semibold text-slate-700">{w.quantity} units</span>
                                              </div>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div className="py-8 text-center text-slate-400 italic text-[11px]">
                                          All active stock has no expiry date.
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* PRICING & PRODUCT INFO */}
                                  <div className="md:col-span-3 flex flex-col gap-2 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
                                    <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                      Pricing & Servings Info
                                    </h4>
                                    <div className="flex items-center justify-between py-1 border-b border-slate-50">
                                      <span className="text-slate-500 font-medium">Product Price:</span>
                                      <span className="font-semibold text-slate-900">
                                        ₹ {row.price?.toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-1 border-b border-slate-50">
                                      <span className="text-slate-500 font-medium">GST Rate:</span>
                                      <span className="font-semibold text-slate-800">
                                        {row.gst}% <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider bg-slate-50 px-1 py-0.5 rounded border border-slate-100 ml-1">
                                          {row.taxInclude ? "Included" : "Excluded"}
                                        </span>
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-1">
                                      <span className="text-slate-500 font-medium">Serving Duration:</span>
                                      <span className="font-semibold text-slate-800">
                                        {row.servings ? `${row.servings} Servings (${row.servingDuration || "N/A"})` : "N/A"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* WAREHOUSE DISTRIBUTION */}
                                  <div className="md:col-span-4 flex flex-col gap-2 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
                                    <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                      Warehouse Distribution
                                    </h4>
                                    <div className="flex flex-col gap-1.5">
                                      {distList.length > 0 ? (
                                        distList.map(([wName, wQty], idx) => (
                                          <div
                                            key={idx}
                                            className="px-2.5 py-1.5 bg-slate-50/70 text-slate-800 border border-slate-100 rounded text-[11px] font-medium flex justify-between items-center"
                                          >
                                            <span className="font-bold text-slate-700">{wName}</span>
                                            <span className="font-black text-[#172554] bg-white px-2 py-0.5 rounded border border-slate-100 shadow-sm">{wQty} units</span>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="py-8 text-center text-slate-400 italic text-[11px]">
                                          No physical warehouse stock.
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={7 + warehouseNames.length}
                  className="py-20 text-center text-slate-400 font-medium italic"
                >
                  No matching inventory records found...
                </td>
              </tr>
            )}
          </tbody>
        </ThemedTable>
      </div>

      <div className="flex justify-center items-center py-8 bg-white border-t border-slate-100">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

    </div>
  );
};

export default InventoryReports;
